import React, { useEffect } from "react";
import { FormattedDate, FormattedMessage, FormattedTime, useIntl } from "react-intl";
import { Bell, Check, MessageSquare, X } from "lucide-react";
import { useDispatch } from "react-redux";
import { setActiveVacancy } from "../../../../../../reducers/active-choice";
import { setIntoSessionStorage } from "../../../../../../utils";
import { ApplicationRoutes, SessionStorageKeys } from "../../../../../constants";
import { useNavigate } from "react-router";
import { setUpdateMatchedProfiles } from "../../../../../../reducers/positions";
import { AdminUserAgent } from "../../../../../../api/adminUserAgent";
import "./index.css";
import { changeActiveChoice } from "../../../../../../reducers/active-choice";
import { escapeRegExp } from "lodash";
import { useInterviewNavigationGuard } from "../../../../../company/interview-builder/interview-navigation-guard";
import { parseUtcDate } from "../../../../../company/utils/dates";

const NotificationBanner = ({ showNotificationBanner, notifications }) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { requestInterviewNavigation } = useInterviewNavigationGuard();
    //Refactor this

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") showNotificationBanner(false);
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [showNotificationBanner]);

    const handleRouteConfiguration = (config) => {
        if (config?.match_status === "pending") {
            dispatch(setActiveVacancy({ title: config?.vacancy_title, id: config?.vacancy_id, candidateId: config?.candidate_id, isRoutedFromNotifications: true, entity_type: config?.entity_type, provider: config?.message_type, vacancy_status: config?.vacancy_status }));
            setIntoSessionStorage(SessionStorageKeys.last_visited_vacancy, JSON.stringify({ title: config?.vacancy_title, id: config?.vacancy_id, candidateId: config?.candidate_id, isRoutedFromNotifications: true, provider: config?.message_type, entity_type: config?.entity_type, vacancy_status: config?.vacancy_status }));
            dispatch(setUpdateMatchedProfiles())
        };
        if (config?.match_status === null && config?.vacancy_status !== "suggested") {
            const vacancyPayload = {
                title: config?.vacancy_title,
                id: config?.vacancy_id,
                candidateId: config?.candidate_id,
                isRoutedFromNotifications: true,
                entity_type: config?.entity_type,
                vacancy_status: config?.vacancy_status,
            };
            const sessionStoragePayload = {
                ...vacancyPayload
            };
            dispatch(setActiveVacancy(vacancyPayload));
            setIntoSessionStorage(SessionStorageKeys.last_visited_vacancy, JSON.stringify(sessionStoragePayload));
            dispatch(setUpdateMatchedProfiles());
        }
        if (config?.vacancy_status === "suggested") {
            dispatch(setActiveVacancy({ title: config?.vacancy_title, id: config?.vacancy_id, isRoutedFromNotifications: true, entity_type: config?.entity_type, vacancy_status: config?.vacancy_status}));
            setIntoSessionStorage(SessionStorageKeys.last_visited_feed_vacancy, JSON.stringify({ title: config?.vacancy_title, id: config?.vacancy_id, isRoutedFromNotifications: true, entity_type: config?.entity_type, vacancy_status: config?.vacancy_status }));            
            window.dispatchEvent(new Event("vacancyNotificationClicked"));
            navigate(ApplicationRoutes.hi5Root);
            showNotificationBanner(false);
        }
    }

    const handleVacancyNavigation = async (config) => {
        requestInterviewNavigation(async () => {
            const token = window.localStorage.getItem("token");

            if (!config?.is_read) {
                try {
                    await AdminUserAgent.markNotificationAsRead(token, config?.id, { is_read: true });
                } catch (error) {
                    console.error("Error marking notification as read:", error);
                }
            }

            if (config?.entity_type === "Candidate" && config?.vacancy_id) {
                navigate(ApplicationRoutes.hi5VacatureDetail(config.vacancy_id), { state: { activeVacancyTab: "kandidaten" } });
                showNotificationBanner(false);
                return;
            }

            navigate(ApplicationRoutes.hi5Root, { state: { isNotRoutedThroughURL: true } });
            showNotificationBanner(false);
            dispatch(changeActiveChoice("Searches"));
            handleRouteConfiguration(config);
        });
    };

    const sortNotifications = (notifications) => {
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        
        // return notifications
        return notifications
            .filter(notification => 
                !notification.message_type || notification.message_type !== 'linkedin'
            )
            .filter(notification => 
                !notification.is_read || parseUtcDate(notification.created_at) > oneDayAgo
            )
            .sort((a, b) => 
                a.is_read === b.is_read ? parseUtcDate(b.created_at) - parseUtcDate(a.created_at) : a.is_read - b.is_read
            );
    };

    const visibleNotifications = sortNotifications(notifications);
    const n_unread = visibleNotifications.filter((notification) => !notification.is_read).length;

    return (
        <section aria-labelledby="notifications-title" className="absolute right-4 md:right-24 top-full mt-3 z-40 w-[420px] max-w-[calc(100vw-48px)] overflow-hidden rounded-[24px] border border-gray-100 bg-white shadow-xl tracking-normal">
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2.5">
                    <h2 id="notifications-title" className="text-[16px] font-bold text-navy-blue"><FormattedMessage id="teamroom.notifications.title" /></h2>
                    {n_unread > 0 && <span className="rounded-full bg-standart-ocean/10 px-2.5 py-1 text-[11px] font-bold text-standart-ocean"><FormattedMessage id="teamroom.notifications.unread" values={{ count: n_unread }} /></span>}
                </div>
                <button type="button" onClick={showNotificationBanner} aria-label={intl.formatMessage({ id: "teamroom.common.close" })} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-[#eef3f8] hover:text-navy-blue transition">
                    <X size={18} strokeWidth={2.4} aria-hidden />
                </button>
            </div>
            {/* {!!notifications.length > 0 ? ( */}
            {visibleNotifications.length > 0 ? (
                <ul className="notification-scrollbar max-h-[min(440px,60vh)] overflow-y-auto p-2 space-y-1">
                    {visibleNotifications.map(({ notification_message, origin_name, entity_type, is_read, ...config }) => {
                        const parts = notification_message.split(new RegExp(`(${escapeRegExp(origin_name)})`, 'gi'));
                        const createdAt = parseUtcDate(config.created_at);
                        return (
                            <li key={config.id}>
                                <button type="button" onClick={() => handleVacancyNavigation({ ...config, entity_type, is_read })} className={`flex w-full gap-3 rounded-[18px] p-3.5 text-left transition ${is_read ? "hover:bg-[#eef3f8]" : "bg-standart-ocean/5 hover:bg-standart-ocean/10"}`}>
                                    <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${is_read ? "bg-[#eef3f8] text-gray-400" : "bg-standart-ocean/10 text-standart-ocean"}`}>
                                        <MessageSquare size={17} strokeWidth={2} aria-hidden />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-[13px] leading-relaxed text-navy-blue break-words">
                                            {parts.map((part, index) => <span key={index} className={part.toLowerCase() === origin_name.toLowerCase() ? "font-bold" : ""}>{part}</span>)}
                                        </span>
                                        <time dateTime={createdAt.toISOString()} className="block mt-1 text-[11px] text-gray-400">
                                            <FormattedDate value={createdAt} day="numeric" month="short" />{" · "}<FormattedTime value={createdAt} hour="2-digit" minute="2-digit" />
                                        </time>
                                    </span>
                                    {!is_read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-standart-ocean" aria-label={intl.formatMessage({ id: "teamroom.notifications.unread" }, { count: 1 })} />}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <div className="flex flex-col items-center px-8 py-9 text-center">
                    <span className="relative inline-flex h-14 w-14 items-center justify-center rounded-[18px] bg-[#eef3f8] text-standart-ocean mb-4">
                        <Bell size={25} strokeWidth={1.8} aria-hidden />
                        <span className="absolute -bottom-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-standart-ocean text-white ring-[3px] ring-white"><Check size={12} strokeWidth={2.6} aria-hidden /></span>
                    </span>
                    <h3 className="text-[14px] font-bold text-navy-blue"><FormattedMessage id="teamroom.notifications.empty" /></h3>
                    <p className="mt-1.5 max-w-[270px] text-[13px] leading-relaxed text-gray-500"><FormattedMessage id="teamroom.notifications.emptyHint" /></p>
                </div>
            )}
        </section>
    )
}

export default NotificationBanner;
